import React, { useCallback, useEffect, useState } from 'react';
import { Button, styleConstants, useFormatDate, useSearchInputStyles, ValidationErrorTooltip } from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import { makeStyles, Theme, useTheme } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { startOfDay, endOfDay } from 'date-fns';
import ReactDatePicker from 'react-datepicker';
import { TimeRangeOptionsFn } from '@shared/models';
import { DatePickerCustomHeader } from './DatePickerCustomHeader';

const useStyles = makeStyles((theme: Theme) => ({
  datePickerContainer: {
    width: '100%',
    minHeight: 40,
    height: 40,
    '& .react-datepicker-wrapper': {
      display: 'block',
      height: '100%',
      minWidth: ({ minWidth }: { minWidth: number }) => minWidth,
      '& input': {
        height: 40,
        padding: theme.spacing(0, 3),
        fontWeight: 600,
        minWidth: ({ minWidth }: { minWidth: number }) => minWidth,
      },
    },
    '& .react-datepicker__input-container': {
      display: 'block',
      height: '100%',
    },
  },
  closeIcon: {
    position: 'absolute',
    right: 8,
    top: 8,
    zIndex: 10,
    cursor: 'pointer',
  },
  calendarContainer: {
    '& .react-datepicker__day--keyboard-selected': {
      backgroundColor: 'transparent',
      color: 'inherit',
      border: 'none',
      outline: 'none',
    },

    '& .react-datepicker__day--selected': {
      backgroundColor: styleConstants.colors.primaryColor,
    },
    '& .react-datepicker__day--in-range': {
      backgroundColor: styleConstants.colors.primaryColor,
    },
    '& .react-datepicker__day--in-selecting-range': {
      backgroundColor: styleConstants.colors.primaryColor + '66',
    },
    '& .react-datepicker__day--in-selecting-range:hover': {
      backgroundColor: styleConstants.colors.primaryColor,
    },
  },
  popper: {
    '&.react-datepicker-popper': {
      paddingTop: 40,
    },
    zIndex: 99999,
  },
}));

interface DatePickerProps {
  values: [Date, Date];
  onChange: (dates: [Date, Date] | undefined) => void;
  singleDate?: boolean;
  minDate?: Date;
  maxDate?: Date;
  openToDate?: Date;
  disabled?: boolean;
  excludeDates?: Date[];
  customFormat?: string;
  customWidth?: number;
  customMinWidth?: number;
  clearable?: boolean;
  newEditableLook?: boolean;
  error?: boolean | string;
  monthAndYearSelect?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  values,
  onChange,
  singleDate = false,
  minDate,
  maxDate,
  openToDate,
  disabled = false,
  excludeDates,
  customFormat,
  customWidth,
  customMinWidth = 196,
  clearable = true,
  newEditableLook = false,
  error = false,
  monthAndYearSelect = false,
}) => {
  const theme = useTheme<Theme>();
  const classes = useStyles({ minWidth: customMinWidth });
  const searchInputStyles = useSearchInputStyles();

  const { formatDate } = useFormatDate();

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(values ? values[0] : null);
  const [endDate, setEndDate] = useState(values ? values[1] : null);

  const _onChange = useCallback(
    (dates: [Date, Date]) => {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
      start && end && onChange?.([startOfDay(start), endOfDay(end)]);
    },
    [onChange]
  );

  const _onChangeSingleDate = useCallback(
    (date: Date) => {
      setStartDate(date);
      date && onChange?.([date, date]);
    },
    [onChange]
  );

  const setDates = useCallback(
    ([start, end]: [Date, Date]) => {
      setStartDate(start);
      setEndDate(end);
      start && end && onChange?.([start, end]);
      setOpen(false);
    },
    [setStartDate, setEndDate, onChange]
  );

  useEffect(() => {
    setStartDate(values ? values[0] : undefined);
    setEndDate(values ? values[1] : undefined);
  }, [values]);

  const inputValue = singleDate
    ? startDate
      ? formatDate(startDate, customFormat ?? 'P')
      : t({ message: `Choose date`, id: '[DatePicker.ChooseDate]' })
    : startDate && endDate
      ? formatDate(startDate, customFormat ?? 'P') + ' - ' + formatDate(endDate, customFormat ?? 'P')
      : t({ message: `Choose range`, id: '[DatePicker.ChooseRange]' });

  return (
    <div
      className={`${searchInputStyles.inputContainer} ${classes.datePickerContainer}`}
      style={customWidth ? { width: customWidth, maxWidth: customWidth } : {}}
    >
      <ValidationErrorTooltip errors={{ err: error }} property={'err'}>
        <ReactDatePicker
          selected={startDate}
          onChange={(dates) => (singleDate ? _onChangeSingleDate(dates as Date) : _onChange(dates as [Date, Date]))}
          startDate={startDate}
          endDate={endDate}
          disabledKeyboardNavigation={true}
          open={open}
          onInputClick={() => {
            setStartDate(values ? values[0] : undefined);
            setEndDate(values ? values[singleDate ? 0 : 1] : undefined);
            setOpen(true);
          }}
          popperClassName={classes.popper}
          selectsRange={!singleDate}
          shouldCloseOnSelect={singleDate || (startDate && !endDate)}
          value={inputValue}
          portalId="root"
          calendarClassName="z-50"
          onCalendarClose={() => {
            if (!((startDate && endDate) || (startDate && singleDate))) {
              setStartDate(values ? values[0] : undefined);
              setEndDate(values ? values[singleDate ? 0 : 1] : undefined);
            }
            setOpen(false);
          }}
          //
          renderCustomHeader={(props) => <DatePickerCustomHeader {...props} monthAndYearSelect={monthAndYearSelect} />}
          //
          calendarContainer={({ children }) => (
            <div className={`bg-white flex flex-col shadow-md ${classes.calendarContainer} z-50`}>
              {children}
              {!singleDate && (
                <div className="flex flex-wrap mb-2 max-w-xs justify-center">
                  {Object.values(TimeRangeOptionsFn()).map((el) => (
                    <Button style={{ height: 28, margin: 2 }} onClick={() => setDates(el.range)}>
                      {el.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          excludeDates={excludeDates}
          minDate={minDate || undefined}
          maxDate={maxDate || undefined}
          openToDate={openToDate || undefined}
          disabled={disabled}
          isClearable={clearable}
          customInput={
            <input
              type="text"
              className="text-center"
              readOnly
              style={{
                color: '#333333',
                textShadow: '0 0 0 ' + theme.palette.text.secondary,
                border: disabled
                  ? '2px solid #fff'
                  : error
                    ? '2px solid red'
                    : newEditableLook
                      ? `2px solid ${styleConstants.colors.primaryColor}`
                      : '',
                borderRadius: disabled ? 0 : '',
                backgroundColor: disabled ? '#F0F1F5' : '',
                cursor: disabled ? '' : 'pointer',
              }}
            />
          }
        />
        {clearable && ((startDate && endDate) || (startDate && singleDate)) && (
          <Close
            className={classes.closeIcon}
            onClick={() => {
              onChange && onChange(undefined);
            }}
          />
        )}
      </ValidationErrorTooltip>
    </div>
  );
};
